<template>
	<v-navigation-drawer
		style="background: rgb(var(--v-theme-contained))"
		location="right"
		:rail="rail"
		rail-width="72"
		permanent
		@click="rail = !rail"
	>
		<v-list id="members-header">
			<v-list-item>
				<template #title>
					<div class="text-h6">{{ symbolsStore.GUILabel("12afad78-3062-4760-abac-e6637f270a1b") }}</div>
				</template>
				<template #prepend>
					<v-icon class="ma-2">{{ rail ? "mdi-account-group-outline" : "mdi-account-group" }}</v-icon>
				</template>
			</v-list-item>
		</v-list>

		<v-divider></v-divider>

		<v-list
			v-for="role in UserRole"
			:key="role"
			:class="{ highlight: dragging && canBeTargetRole(role), transparent: !dragging }"
			style="background-color: transparent"
			density="compact"
			nav
			@dragover="dragOver"
			@dragenter="dragEnter"
			@dragleave="dragLeave"
			@drop="(e: any) => drop(e, role)"
		>
			<v-layout>
				<v-card-subtitle class="ma-2 my-3">
					{{ roleMembers(role).length }}
					{{ rail ? "" : role.toUpperCase() + (roleMembers(role).length == 1 ? "" : "S") }}
				</v-card-subtitle>
				<v-spacer />
				<div style="width: 40px">
					<v-btn
						v-if="!rail && domainStore.canModify(role) && domainStore.isAdmin"
						size="small"
						variant="text"
						color="#8D8E8F"
						flat
						icon="mdi-account-multiple-plus"
						@click="(e: MouseEvent) => showMemberModal(e, role)"
					></v-btn>
				</div>
			</v-layout>
			<v-list-item
				v-for="member in roleMembers(role)"
				:key="member.id"
				:draggable="!rail && true"
				class="ml-1"
				@click="(e: KeyboardEvent | MouseEvent) => memberClick(e)"
				@dragstart="(e: MouseEvent) => dragStart(e, member)"
				@dragend="(e: MouseEvent) => dragEnd(e)"
				@mouseover="hoverMember = member.id"
				@mouseleave="hoverMember = null"
			>
				<template #prepend>
					<Avatar :color="member.color" :text="member.firstname[0] + member.lastname[0]" :size="32" />
				</template>
				<div class="ma-1">
					<v-list-item-title v-show="!rail"> {{ member.firstname }} {{ member.lastname }} </v-list-item-title>
					<v-list-item-subtitle v-show="!rail">{{ member.email }}</v-list-item-subtitle>
				</div>
				<template
					v-if="
						!rail && member.id == hoverMember && member.id != domainStore.account.id && domainStore.isAdmin
					"
					#append
				>
					<v-btn
						size="small"
						variant="text"
						color="#8D8E8F"
						icon="mdi-close"
						@click="domainStore.removeMember(member.id)"
					/>
				</template>
			</v-list-item>
		</v-list>
	</v-navigation-drawer>
	<AddMemberModal
		:show="addMemberShow"
		:role="addMemberRole"
		@add-member="(user: User, role: UserRole) => domainStore.addMember(user, role)"
		@done="() => (addMemberShow = false)"
	/>
</template>

<script setup lang="ts">
import { type User, UserRole } from "@lr-notitia/notitia-operator/schemas";
import { ref } from "vue";
import Avatar from "../components/Avatar.vue";
import AddMemberModal from "../components/AddMemberModal.vue";
import { useDomainStore } from "../store/domain";
import { useSymbolsStore } from "../store/symbols";

const domainStore = useDomainStore();
const symbolsStore = useSymbolsStore();

const rail = ref(true);
const hoverMember = ref<string | null>(null);
const dragging = ref<boolean>(false);

let addMemberShow = ref(false);
let addMemberRole = ref(UserRole.Viewer);

function roleMembers(role: string) {
	if (!domainStore.members) return [];
	return domainStore.members.filter((m: any) => m.role === role);
}

function memberClick(e: any) {
	e.stopPropagation();
}

function showMemberModal(e: any, role: UserRole) {
	e.stopPropagation();
	if (domainStore.canModify(role)) {
		addMemberShow.value = true;
		addMemberRole.value = role;
	}
}

function canBeTargetRole(role: UserRole): boolean {
	if (domainStore.isAdmin || role == UserRole.Viewer) return true;
	return false;
}

// drag & drop
function dragStart(e: any, member: User) {
	if (!domainStore.canModify(member.role)) return;
	if (domainStore.account.id === member.id) {
		e.preventDefault();
		return;
	}
	dragging.value = true;
	e.srcElement.style.opacity = "0.5";
	e.dataTransfer.effectAllowed = "move";
	e.dataTransfer.setData("member", JSON.stringify(member));
}
function dragEnd(e: any) {
	e.srcElement.style.opacity = "1";
	dragging.value = false;
}
function dragOver(e: any) {
	e.preventDefault();
}
function dragEnter(e: any) {
	e.preventDefault();
}
function dragLeave(e: any) {
	e.preventDefault();
}
function drop(e: any, role: UserRole) {
	e.preventDefault();
	let user = JSON.parse(e.dataTransfer.getData("member"));
	domainStore.moveMember(user, role);
}
</script>

<style scoped lang="scss">
.transparent {
	border: 1px solid transparent;
}
.highlight {
	border: 1px solid rgb(var(--v-theme-primary));
	border-radius: 0.5em;
}
</style>
